import { cx } from "@zenchef/styled-system/css";
import { Box, BoxProps } from "@zenchef/styled-system/jsx";
import {
  aromaDraggableItem,
  AromaDraggableItemVariantProps,
} from "@zenchef/styled-system/recipes";
import React, { useRef, useEffect, useContext } from "react";
import { Icon } from "../index";
import { DraggableContext } from "./DraggableContainer";
import Signifier from "./Signifier";
import useDragAndDrop from "./useDragAndDrop";

export interface DraggableItemProps
  extends AromaDraggableItemVariantProps,
    Omit<BoxProps, "id"> {
  children: React.ReactNode;
  id: string | number;
  disabled?: boolean;
}

export interface DraggableItemData {
  id: number | string;
  [key: string | symbol]: unknown;
}

const DraggableItem = ({
  children,
  disabled,
  id,
  ...propsWithVariants
}: DraggableItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);

  const context = useContext(DraggableContext);
  if (!context) {
    throw new Error("DraggableItem must be used within DraggableContainer");
  }
  const { moveItem, flow } = context;

  const [variantProps, props] =
    aromaDraggableItem.splitVariantProps(propsWithVariants);

  const dragState = useDragAndDrop({
    id,
    itemRef,
    dragHandleRef,
    disabled,
    variantProps,
    flow,
  });

  const classes = aromaDraggableItem({
    ...variantProps,
    ...(dragState.type === "dragging" ? { ghost: true } : {}),
  });

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (document.activeElement !== itemRef.current) return;
      // We want controls to be consistent for vertical and horizontal flow, so the same buttons are used for both
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        moveItem(id, "up");
      }
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        moveItem(id, "down");
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [moveItem]);

  return (
    <Box
      position="relative"
      display="flex"
      cursor="grab"
      {...props}
      ref={dragHandleRef}
    >
      {dragState.type === "dragging-over" && dragState.closestEdge ? (
        <Signifier edge={dragState.closestEdge} />
      ) : null}

      <Box
        ref={itemRef}
        className={cx(classes.wrapper, "group", props.className)}
        data-disabled={disabled}
        data-draggable-id={id}
        draggable
        tabIndex={disabled ? -1 : 0}
      >
        <Box className={classes.knob} data-part="knob">
          <Icon name="drag-handle" />
        </Box>
        <Box className={classes.content}>{children}</Box>
      </Box>
    </Box>
  );
};

export default DraggableItem;
